export interface NormalizedPaperAuthor {
    name: string;
    orcid?: string | null;
}

function normalizeString(value: unknown): string | null {
    if (typeof value !== 'string') {
        return null;
    }

    const normalized = value.trim();
    return normalized ? normalized : null;
}

function normalizeAuthorRecord(author: Record<string, unknown>): NormalizedPaperAuthor | null {
    const name = normalizeString(author.name)
        || normalizeString(author.full_name)
        || normalizeString(author.author);

    if (!name) {
        return null;
    }

    const orcid = normalizeString(author.orcid);
    return orcid ? { name, orcid } : { name };
}

function normalizeAuthorEntry(author: unknown): NormalizedPaperAuthor | null {
    const stringAuthor = normalizeString(author);
    if (stringAuthor) {
        return { name: stringAuthor };
    }

    if (!author || typeof author !== 'object') {
        return null;
    }

    return normalizeAuthorRecord(author as Record<string, unknown>);
}

function getRawAuthorEntries(authors: unknown): unknown[] {
    if (Array.isArray(authors)) {
        return authors;
    }

    const stringAuthor = normalizeString(authors);
    if (stringAuthor) {
        return [stringAuthor];
    }

    if (!authors || typeof authors !== 'object') {
        return [];
    }

    const authorRecord = authors as Record<string, unknown>;
    if (authorRecord.name || authorRecord.full_name || authorRecord.author) {
        return [authorRecord];
    }

    return Object.values(authorRecord);
}

export function normalizePaperAuthors(authors: unknown): NormalizedPaperAuthor[] | null {
    const normalizedAuthors = getRawAuthorEntries(authors)
        .map((author) => normalizeAuthorEntry(author))
        .filter((author): author is NormalizedPaperAuthor => author !== null);

    return normalizedAuthors.length > 0 ? normalizedAuthors : null;
}

export function getPaperAuthorNames(authors: unknown): string[] {
    return normalizePaperAuthors(authors)?.map((author) => author.name) ?? [];
}

export function formatPaperAuthors(
    authors: unknown,
    emptyLabel = 'Authors not available',
    maxVisibleAuthors = 5
): string {
    const names = getPaperAuthorNames(authors);

    if (names.length === 0) {
        return emptyLabel;
    }

    if (names.length <= maxVisibleAuthors) {
        return names.join(', ');
    }

    return `${names.slice(0, maxVisibleAuthors).join(', ')}, et al.`;
}

export function normalizePaperData<T extends { authors?: unknown | null }>(
    paper: T
): Omit<T, 'authors'> & { authors: NormalizedPaperAuthor[] | null } {
    return {
        ...paper,
        authors: normalizePaperAuthors(paper.authors),
    };
}
